import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { FileSpreadsheet, Link2, CheckCircle2 } from "lucide-react";

const unitKey = (tipe, nama) => `${tipe}::${nama}`;

function UnitRow({ tipe, nama, entry, onSave }) {
  const [url, setUrl] = useState(entry?.url || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setUrl(entry?.url || ""); }, [entry?.url]);
  const mapped = !!entry?.spreadsheet_id;
  const submit = async () => {
    setSaving(true);
    try { await onSave(tipe, nama, url.trim()); } finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`rab-map-row-${tipe}-${nama}`}>
      <span className="w-40 shrink-0 text-xs text-slate-700 dark:text-slate-200 truncate flex items-center gap-1">
        {mapped ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> : <Link2 className="h-3.5 w-3.5 text-slate-300 shrink-0" />}
        {nama}
      </span>
      <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Tempel link Google Sheet unit ini (kosongkan untuk hapus)"
        className="flex-1 min-w-[200px] h-9 text-xs" data-testid={`rab-map-input-${tipe}-${nama}`} />
      <Button size="sm" variant="secondary" className="h-9" disabled={saving} data-testid={`rab-map-save-${tipe}-${nama}`} onClick={submit}>Simpan</Button>
    </div>
  );
}

export default function RabSheetMapManager() {
  const qc = useQueryClient();
  const { data: options = {} } = useQuery({ queryKey: ["options"], queryFn: async () => (await api.get("/options")).data });
  const { data: cfg = { map: {}, connected: false } } = useQuery({ queryKey: ["rab-sheet-map"], queryFn: async () => (await api.get("/keuangan/rab/sheet-map")).data });
  const save = useMutation({
    mutationFn: async ({ unit_tipe, unit_nama, sheet_url }) => api.post("/keuangan/rab/sheet-map", { unit_tipe, unit_nama, sheet_url }),
    onSuccess: (_r, v) => { qc.invalidateQueries({ queryKey: ["rab-sheet-map"] }); toast.success(v.sheet_url ? "Pemetaan disimpan" : "Pemetaan dihapus"); },
    onError: (e) => toast.error(errMsg(e, "Gagal menyimpan")),
  });
  const onSave = (unit_tipe, unit_nama, sheet_url) => save.mutateAsync({ unit_tipe, unit_nama, sheet_url });

  const departemen = options.departemen || [];
  const ribath = options.ribath || [];
  const mappedCount = useMemo(() => Object.keys(cfg.map || {}).length, [cfg.map]);

  return (
    <div className="glass rounded-2xl p-6 space-y-4" data-testid="rab-sheet-map-manager">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300"><FileSpreadsheet className="h-5 w-5" /><span className="font-bold text-lg">Pemetaan Sheet RAB per Unit</span></div>
        <Badge className="border-0 bg-emerald-100 text-emerald-700">{mappedCount} unit terpetakan</Badge>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-300">Tempel link Google Sheet untuk tiap Departemen & Ribath. Setiap RAB yang dibuat/diedit/di-ACC otomatis ditulis (satu arah) ke sheet unitnya sebagai tab per kegiatan. Beri akses <strong>Editor</strong> ke akun Google yang terhubung.</p>
      {!cfg.connected && <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-2" data-testid="rab-map-warning">Google Sheets belum terhubung. Pemetaan tetap tersimpan, namun penulisan otomatis baru berjalan setelah Google Sheets dihubungkan di bagian atas halaman ini.</div>}

      <div className="space-y-2">
        <p className="font-semibold text-emerald-900 dark:text-emerald-100 text-sm">Departemen ({departemen.length})</p>
        <div className="space-y-1.5">
          {departemen.map((n) => <UnitRow key={n} tipe="departemen" nama={n} entry={cfg.map[unitKey("departemen", n)]} onSave={onSave} />)}
          {departemen.length === 0 && <p className="text-xs text-slate-400">Belum ada daftar departemen di Pengaturan → Kategori.</p>}
        </div>
      </div>
      <div className="space-y-2 border-t border-emerald-100 dark:border-white/10 pt-3">
        <p className="font-semibold text-emerald-900 dark:text-emerald-100 text-sm">Ribath ({ribath.length})</p>
        <div className="space-y-1.5">
          {ribath.map((n) => <UnitRow key={n} tipe="ribath" nama={n} entry={cfg.map[unitKey("ribath", n)]} onSave={onSave} />)}
          {ribath.length === 0 && <p className="text-xs text-slate-400">Belum ada daftar ribath di Pengaturan → Kategori.</p>}
        </div>
      </div>
    </div>
  );
}
